import {makeAutoObservable, runInAction} from 'mobx';
import type {RootStore} from 'stores/rootStore';
import {claimGuestCheckout, createStripeSession, customerPortalRequest, getPromoOffer, guestCheckoutSession, paymentDetailsRequest, PromoOffer, waitForPaymentRequest} from 'requests';
import {isScanRoute, paramsGet, paramsRemove, sleep, toSubKey} from 'utils';
import {logEvent} from 'analytics';
import {PaymentDetails, Plan, PlanPeriod} from "types";
import {gtmCheckoutStarted, gtmEvent, gtmInlineCheckout, gtmPaymentCompleted, gtmStripeRedirectStarted} from "utils/gtm";
import {getDefaultBillingPeriod, getAssignedABVariant, showsTrialOffer} from "utils/abTest";
import {measurementId} from 'utils/funnel';
import {safeStorage} from 'lib/safeStorage';
import {consumeCheckoutPage, hasCheckoutPage, persistCheckoutPage, type CheckoutPageDraft} from 'persistence';
import type {AppBillingReconcile} from "types";
import {billingErrorText} from 'utils/billingErrors';


/** What a finished checkout has to tell the buyer, out of what the backend settled and
 *  what the account looks like afterwards. One table, because the same purchase can end
 *  three ways and both places that finish a checkout — the claim paths here and the
 *  paywall's inline checkout — owe the buyer the same answer.
 *
 *  `reconcile` is the backend's verdict, and outranks everything: it alone distinguishes
 *  a refused trial from one that simply hasn't started yet. The anon login path never
 *  gets it (@it-s-ai/auth relays no outcome to onLoginComplete), so there `soldTrial`
 *  says what was bought and `trialing` says whether it survived — authoritative on that
 *  path because the claim grants entitlement in the same request rather than from a
 *  later webhook. */
export function confirmTypeFor(
    {reconcile, soldTrial, trialing}:
    {reconcile?: AppBillingReconcile | null; soldTrial?: boolean; trialing?: boolean},
): 'refunded' | 'trial_declined' | 'trial' | 'activated' {
    if (reconcile === 'refunded_existing') return 'refunded';
    if (reconcile === 'trial_declined') return 'trial_declined';
    if (soldTrial && !trialing) return 'trial_declined';
    return trialing ? 'trial' : 'activated';
}


//: Where a promo code from an email link is kept. sessionStorage, not the store:
//: a reader who arrives signed out is sent through login first, and the redirect
//: back does not bring the query string with it.
const PROMO_KEY = 'email_promo_code';

export class SubscriptionStore {
    root: RootStore;
    plan: 'monthly' | 'yearly' = 'yearly';
    highlightedPlan: Plan | null = null;
    paymentDetails: PaymentDetails | null = null;
    autoPayHighlighted: boolean = false;
    /** The remembered promo code, as Stripe values it; null until known to be live. */
    promo: PromoOffer | null = null;
    private promoAsked: string | null = null;

    constructor(root: RootStore) {
        this.root = root;
        makeAutoObservable(this);
    }

    /** Remember a `?promo=` an email CTA arrived with, for this browser session. */
    rememberPromoCode = (code: string) => safeStorage(() => sessionStorage.setItem(PROMO_KEY, code));

    // A method, not a getter: makeAutoObservable turns a getter into a computed,
    // and sessionStorage is not something mobx can watch.
    getPromoCode = (): string | undefined =>
        safeStorage(() => sessionStorage.getItem(PROMO_KEY) || undefined);

    forgetPromoCode = () => {
        safeStorage(() => sessionStorage.removeItem(PROMO_KEY));
        this.promo = null;
        this.promoAsked = null;
    };

    loadPromo = async () => {
        const code = this.getPromoCode();
        if (!code || code === this.promoAsked) return;
        this.promoAsked = code;
        // A stale quote must not name the previous code while this one is asked.
        this.promo = null;
        let promo: PromoOffer | null = null;
        try {
            promo = await getPromoOffer(code);
        } catch {
            // Quoting is a courtesy: checkout still applies the code without it.
            this.promoAsked = null;
        }
        // The code may have been spent while Stripe was being asked.
        runInAction(() => {
            if (this.getPromoCode() === code) this.promo = promo;
        });
    };

    /** Percent the promo takes off a new subscription billed this way, 0 when it does not apply.
     *  New only: on a plan change the code rides a prorated modify, whose amount no card shows. */
    promoPercent = (period: PlanPeriod): number =>
        this.root.user.canStartSubscription && this.promo && (!this.promo.period || this.promo.period === period)
            ? this.promo.percent_off : 0;

    loadPaymentDetails = async () => {
        try {
            this.paymentDetails = await paymentDetailsRequest();
        } catch (e) {
            if (this.paymentDetails || !this.root.user.isFree()) {
                this.root.setError(billingErrorText(e, 'details'));
            }
        }
    };

    // After an inline payment, the subscription goes active via the Stripe
    // webhook (not synchronously). Poll our own state until it lands — capped,
    // and stops as soon as the user is no longer Free — so the UI reflects
    // premium without a blind page reload. Safe to fire-and-forget.
    //
    // `plan` waits for that specific tier instead: an upgrade of an existing
    // subscription starts out already non-Free, so the isFree() test would pass
    // on the first poll and report a plan that hasn't landed yet.
    //
    // Answers whether it landed, so a caller can tell "premium is live" from
    // "we gave up waiting" — the buyer paid either way and the two owe them
    // different screens.
    //
    // Only /users/me is polled: it alone carries the plan this loop is waiting
    // for. payments/details answers nothing here and costs three Stripe calls
    // per look, so it is fetched once — when the plan has actually changed and
    // the card / invoice / renewal date it feeds are finally new.
    waitForActiveSubscription = async (plan?: string, attempts = 6, delayMs = 1500): Promise<boolean> => {
        for (let i = 0; i < attempts; i++) {
            try {
                await this.root.user.refresh();
            } catch {
                // A failed status poll is pending activation, not a failed payment.
                return false;
            }
            const landed = plan
                ? toSubKey(this.root.user.user?.subscription) === plan
                : !this.root.user.isFree();
            if (landed) {
                await this.loadPaymentDetails();
                return true;
            }
            // Not after the last one: that sleep delays the caller's answer — and
            // the screen it owes the buyer — without buying another look.
            if (i < attempts - 1) await sleep(delayMs);
        }
        return false;
    };

    setPlan(plan: 'monthly' | 'yearly') {
        this.plan = plan;
    }

    async openCustomerPortal() {
        this.root.setLoading(true);
        try {
            const {url} = await customerPortalRequest();
            window.open(url);
        } catch (e) {
            this.root.toast.error('billing-toast-portal', e, billingErrorText(e, 'portal'));
        } finally {
            this.root.setLoading(false);
        }
    }

    highlightPlan(plan: Plan | null) {
        this.highlightedPlan = plan;
    }

    // Fire the funnel events for a Stripe checkout redirect — shared by the
    // authenticated change-plan flow and anonymous guest checkout. Awaits the
    // redirect event so it's delivered before navigation.
    private async logCheckoutRedirect(opts: {plan: string; period: 'monthly' | 'yearly'; id: string; trial: boolean; entrypoint: string; measurement?: Record<string, string | undefined>}) {
        const {plan, period, id, trial, entrypoint, measurement = {}} = opts;
        const value = this.root.pricing.PRICING[period]?.[plan as Plan]?.price ?? 0;
        // `value` is a catalogue price, and the catalogue is priced in the
        // visitor's currency — reporting it as USD told analytics that 588 AED
        // was 588 dollars.
        const currency = (this.root.pricing.currency || 'usd').toUpperCase();
        const billing_default_variant = getDefaultBillingPeriod('checkout');
        logEvent(gtmCheckoutStarted({
            ...measurement, checkout_session_id: id,
            plan_name: plan, billing_period: period, payment_provider: 'stripe',
            value, currency, trial, page_type: 'subscriptions', entrypoint, billing_default_variant,
        }));
        await logEvent(gtmStripeRedirectStarted({
            ...measurement,
            checkout_session_id: id, plan_name: plan, plan_key: plan, billing_period: period,
            value, currency, trial, source: 'app_subscription', page_type: 'subscriptions', entrypoint,
            billing_default_variant,
        }));
    }

    // Pay-first guest checkout: send an anonymous user straight to Stripe with
    // no account. The Stripe Customer is parked on their anon row server-side;
    // on return they verify (OTP/Google) and the billing is moved onto the
    // durable account. See guest_checkout.py.
    //
    // The 7-day trial may ride along, and a guest may start one without an
    // account: it takes no money on day one, so there is nothing to collect an
    // email for first. Whether to ask for it is decided here, off the same
    // predicate the plans grid labels its CTA with, so the button cannot offer
    // what the request doesn't ask for. The server decides whether it is granted,
    // and re-decides it against the real account when the visitor signs in — a
    // trial that turns out not to be theirs is cancelled there
    // (reconcile_guest_billing).
    //
    // Returns 'already_pending' when the anon already has a paid-but-unclaimed
    // guest subscription: the backend refuses to open a second checkout (no
    // double charge), so the caller routes the user to the claim flow instead.
    async startGuestCheckout(plan: string, period: 'monthly' | 'yearly'): Promise<'redirected' | 'already_pending' | 'error'> {
        await logEvent(gtmEvent('GTM_subscription_guest_checkout', {plan_key: plan, billing_period: period, page_type: 'subscriptions', entrypoint: 'guest_checkout'}));
        this.root.setLoading(true);
        try {
            const trial = showsTrialOffer(this.root.user.user, plan);
            const {url, id, already_pending, trial_applied} = await guestCheckoutSession(plan, period, trial, this.getPromoCode());
            if (already_pending) {
                this.root.setLoading(false);
                // Surface the parked subscription so the claim CTA appears on the
                // subscription page; the caller reopens the auth modal.
                await this.loadPaymentDetails();
                return 'already_pending';
            }
            // The server is authoritative on whether the trial was actually applied —
            // same as the authenticated redirect above, and for the same reason: the
            // funnel must count what was sold, not what the button asked for.
            await this.logCheckoutRedirect({plan, period, id, trial: !!trial_applied, entrypoint: 'guest_checkout'});
            window.location.assign(url);
            return 'redirected';
        } catch (e) {
            // Plan not purchasable as guest / network error: surface it instead
            // of silently bouncing the user to the plans page.
            this.root.setLoading(false);
            this.root.toast.error('billing-toast-checkout', e, billingErrorText(e, 'checkout'));
            return 'error';
        }
    }

    // Logged-in user opened the guest-checkout claim email: attach the purchase
    // to their current account (backend verifies the session email matches).
    // Refresh + confirm what the claim actually did. Shared by the guest-claim
    // paths (logged-in claim here, post-verify in LoginCompleteHandler).
    //
    // Not every claim is an activation — see confirmTypeFor. Each caller passes the
    // evidence it actually has: the claim response carries the backend's verdict, the
    // login path only knows what was bought.
    async confirmActivated(evidence: {reconcile?: AppBillingReconcile | null; soldTrial?: boolean} = {}) {
        await this.root.user.refresh();
        this.root.modal.showModal('subscription-confirm', {
            confirmType: confirmTypeFor({...evidence, trialing: !!this.root.user.user?.is_trialing}),
            currSub: this.root.user.user?.subscription,
        });
    }

    async claimGuestCheckoutReturn(sessionId: string) {
        this.root.setLoading(true);
        try {
            const {billing_reconcile} = await claimGuestCheckout(sessionId);
            this.root.setLoading(false);
            await this.confirmActivated({reconcile: billing_reconcile});
        } catch (e) {
            // e.g. email mismatch / already claimed — surface rather than hang.
            this.root.setLoading(false);
            this.root.setError(billingErrorText(e, 'claim'));
        }
    }

    /** The page a paywall blocked, as the user left it: the tab, the text or files, the result
     *  on screen. Hosted checkout is a full redirect, so the paywall's `onUnblocked` dies with
     *  the page — and the action is not re-run on return either: the user takes it from there.
     *  Null off the scan page or with nothing on it: there is no page to bring the buyer back to. */
    private snapshotPage(): CheckoutPageDraft | null {
        const {input, batchFile, humanizer, layout} = this.root;
        const humanizerText = layout.humanizerOpen ? humanizer.inputText.trim() : '';
        if (!isScanRoute() || !(input.hasAnyText || batchFile.isActive || humanizerText)) return null;
        return {
            section: input.activeSection,
            text: input.text,
            modes: input.scanModes,
            analysis: batchFile.isBatchMode ? null : input.activeStore.analysisUuid,
            files: batchFile.getPersistableUploadedFiles(),
            filesModes: batchFile.modes,
            ranBatch: {files: batchFile.ranFiles(), modes: batchFile.modes, selected: batchFile.selectedFile},
            humanizer: layout.humanizerOpen ? {text: humanizer.inputText, mode: humanizer.mode} : null,
        };
    }

    /** The scan page, back from a paywall's checkout: its page returns, in the order the login
     *  restore uses (LoginCompleteHandler). */
    restoreParkedPage() {
        const page = consumeCheckoutPage();
        if (!page) return;
        const {input, batchFile, humanizer, layout} = this.root;
        // What this load already brought (a landing hand-off, a login's draft) is the fresher intent.
        if (input.hasAnyText || batchFile.isActive || humanizer.inputText.trim()) return;
        layout.setWorkspace(page.humanizer ? 'humanizer' : 'scan');
        if (page.humanizer?.text) humanizer.restoreDraft({...page.humanizer, autoRun: false});
        if (page.files.length) {
            batchFile.injectUploadedFiles(page.files);
            input.applyScanModes(page.filesModes);
        } else if (page.text && !page.ranBatch.files.length) {
            input.applyScanModes(page.modes);
            input.setActiveSection(page.section);
            void input.restoreDraft(page);
        }
        if (page.ranBatch.files.length) void batchFile.restoreRan(page.ranBatch);
    }

    /** Resolves true when the checkout was started from a paywall and the user belongs back on its page. */
    async handleStripeReturn(): Promise<boolean> {
        const stripeStart = paramsGet('stripe');
        if (!stripeStart) return false;
        const success = paramsGet('success');
        // Checkout abandoned or declined (Stripe cancel / 3DS fail): no `success`,
        // only a `canceled` flag. Clean the URL so the params don't linger — no
        // "subscription cancelled" modal (they didn't cancel a subscription).
        if (!success) {
            if (paramsGet('canceled')) {
                paramsRemove('stripe', 'canceled', 'payment_type', 'session_id');
            }
            return hasCheckoutPage();
        }
        const sessionId = paramsGet('session_id');
        // Guest checkout return is handled at the React level by
        // GuestCheckoutReturnHandler — for an anonymous user (opens the auth
        // modal) or a logged-in user opening the claim email (`claim` flag).
        // Skip the authenticated confirmation flow in both cases.
        if (sessionId && (this.root.user.isAnonymous || paramsGet('claim'))) return false;
        const payment_type = paramsGet('payment_type');

        const handleSubscriptionChange = async (trialApplied = false) => {
            paramsRemove('stripe', 'success', 'payment_type', 'session_id');

            this.root.setLoading(false);

            if (payment_type === 'pay_as_you_go') {
                this.root.modal.showModal('subscription-confirm', {
                    confirmType: 'pay_as_you_go',
                    currSub: this.root.user.user?.subscription
                });
            } else {
                // Same table the claim paths and the paywall confirm from. `trialApplied`
                // is the server's word on this checkout session; `is_trialing` covers a
                // wait that timed out before the session could say. Read after /users/me
                // has been refreshed.
                this.root.modal.showModal('subscription-confirm', {
                    confirmType: confirmTypeFor({trialing: trialApplied || !!this.root.user.user?.is_trialing}),
                    currSub: this.root.user.user?.subscription
                });
                if (toSubKey(this.root.user.user?.subscription) === "enterprise") {
                    this.autoPayHighlighted = true;
                }
            }
        };

        this.root.setLoading(true);
        if (!sessionId) {
            await handleSubscriptionChange();
            return hasCheckoutPage();
        }

        try {
            // Server is authoritative: returns trial_applied based on the
            // actual Stripe subscription's trial_start field.
            const {trial_applied} = await waitForPaymentRequest(sessionId);
            await this.root.user.refresh();
            const pName = payment_type || this.root.user.user?.subscription || 'unknown';
            const pPeriod = (this.root.user.user?.subscription_period || 'monthly') as 'monthly' | 'yearly';
            logEvent(gtmPaymentCompleted({
                checkout_session_id: sessionId,
                plan_name: pName,
                billing_period: pPeriod,
                payment_provider: 'stripe',
                value: this.root.pricing.PRICING[pPeriod]?.[pName as Plan]?.price ?? 0,
                currency: (this.root.pricing.currency || 'usd').toUpperCase(),
                trial: Boolean(trial_applied),
                page_type: 'checkout_success',
                entrypoint: 'stripe_checkout',
                billing_default_variant: getDefaultBillingPeriod('checkout_success'),
            }));
            await this.loadPaymentDetails();
            await handleSubscriptionChange(Boolean(trial_applied))
            return hasCheckoutPage();
        } catch (e) {
            this.root.setLoading(false);
            this.root.setError(billingErrorText(e, 'activation'));
            return hasCheckoutPage();
        }
    }

    // A new subscription on a saved card is paid in the app: hosted Checkout skips cards with no
    // billing country, which our form never asks, so the buyer would type it again. An email's
    // promo code applies only to a Checkout session, so that purchase still goes there.
    private async paysOnSavedCard(plan: string, period: PlanPeriod): Promise<boolean> {
        const {user} = this.root;
        const token = user.token;
        if (!user.canStartSubscription || user.isAnonymous || !token || this.getPromoCode()) return false;
        // An email link's plan is whatever the URL says; the server refuses one we don't sell.
        if (!this.root.pricing.PRICING[period]?.[plan as Plan]) return false;
        // The same read the checkout opens on, so it shows these cards without asking again.
        // A chunk that fails to load leaves the redirect, which needs none of it.
        const cards = await import('pages/ScanPage/oneTimeCharge')
            .then(m => m.prefetchCards(token)).catch(() => []);
        return cards.length > 0;
    }

    /** `onChanged` runs once the new plan is live and /users/me has been refreshed — the paywall
     *  passes the run it interrupted, so an upgrade continues into the scan instead of dropping
     *  the user back on the page to press Run scan again. A hosted checkout leaves the page, so
     *  that run is lost with it: the page itself is parked instead (snapshotPage). */
    async changePlan(plan: string, period: 'monthly' | 'yearly', preview: boolean = true, entrypoint: string = 'subscription_page', onChanged?: () => void) {
        this.root.setLoading(true);
        if (!preview) {
            this.highlightPlan(null);
        }
        let measurement: Record<string, string | undefined> = {};
        const failed = (e: unknown) =>
            this.root.toast.error(plan === 'free' ? 'billing-toast-cancel' : 'billing-toast-subscription', e,
                billingErrorText(e, 'checkout'));
        try {
            // Trial is a Premium-only offer, and only for the arm and the accounts
            // that still have one — showsTrialOffer holds all three halves.
            const trialRequested = showsTrialOffer(this.root.user.user, plan);
            // The other arm's offer, so never asked for alongside the trial —
            // isDiscountActive() is already 0 for the trial arm, and the server
            // would drop the promo anyway (checkout_price_name).
            const discountPercent = plan === 'premium' && period === 'yearly' && this.root.user.isDiscountActive() ? 40 : 0;
            // Sent on a preview too: a first purchase answers `preview` with a real
            // checkout session (there is nothing to prorate), and that is exactly the
            // path the annual letter takes — withholding the code there dropped it.
            measurement = {
                checkout_id: measurementId(),
                trial_variant: getAssignedABVariant('trial'),
                offer_kind: trialRequested ? 'trial' : discountPercent ? 'first_limit_discount' : 'standard',
            };
            const onSavedCard = this.paysOnSavedCard(plan, period);
            await logEvent(gtmEvent('GTM_subscription_get_started', {
                ...measurement, plan_key: plan, billing_period: period, preview, entrypoint,
                page_type: entrypoint === 'limits_reached_popup' ? 'app_scan' : 'subscriptions',
            }));
            if (await onSavedCard) {
                this.root.setLoading(false);
                this.root.modal.showModal('increase-limits-form', {plan, period});
                return;
            }
            const {url, type, trial_applied, ...params} = await createStripeSession(
                plan, period, preview, discountPercent, trialRequested, this.getPromoCode(), measurement);
            // Server is authoritative on whether the trial was actually
            // applied — funnel events and the post-redirect handoff use this,
            // not the local request flag.
            const trial = Boolean(trial_applied);
            this.root.setLoading(false);

            if (type === 'preview') {
                this.root.modal.showModal('subscription-change-preview', {
                    params,
                    period,
                    plan,
                    onOk: () => this.changePlan(plan, period, false, entrypoint, onChanged),
                });
            } else if (type === 'failed') {
                window.open(url);
            } else if (type === 'new') {
                await this.logCheckoutRedirect({plan, period, id: params.id, trial, entrypoint, measurement});
                this.forgetPromoCode();
                persistCheckoutPage(entrypoint === 'limits_reached_popup' ? this.snapshotPage() : null);
                window.location.assign(url);
            } else if (type === 'changed') {
                this.forgetPromoCode();
                await this.root.user.refresh();
                await this.loadPaymentDetails();
                if (params.change_type === 'cancelled') {
                    this.root.modal.showModal('subscription-confirm', {
                        confirmType: 'cancelled',
                        currSub: this.root.user.user?.subscription
                    });
                } else {
                    this.root.modal.showModal('subscription-confirm', {
                        confirmType: params.change_type,
                        currSub: params.change_type === 'downgraded' ? params.prev_plan : params.plan
                    });
                    // The wire label is not just 'upgraded': the backend relabels a
                    // trial → paid change as 'activated' and a yearly → monthly one as
                    // 'yearly_to_monthly' (payments/views.py). All three mean the plan
                    // we just sold is live, and gating on 'upgraded' alone silently
                    // dropped the resume for every trialing buyer — the one thing this
                    // paywall path exists to do. Only 'downgraded' must not resume.
                    if (['upgraded', 'activated', 'yearly_to_monthly'].includes(params.change_type)) {
                        if (params.plan === "enterprise") this.autoPayHighlighted = true;
                        if (onChanged) {
                            // The entitlement itself lands via the webhook, which the backend
                            // only waits ~15s for before answering. Poll for the tier we sold
                            // before re-running, or a lagging reconcile bounces the buyer
                            // straight back into the wall they just paid to clear.
                            await this.waitForActiveSubscription(plan);
                            onChanged();
                        }
                    }
                }
            } else {
                console.log('Unknown response type', type);
                failed(null);
            }
        } catch (e) {
            console.error('Checkout session failed', e);
            logEvent(gtmInlineCheckout('GTM_inline_checkout_upgrade_error', {
                ...measurement,
                plan_key: plan,
                billing_period: period,
                error_message: String((e as {message?: string})?.message ?? e),
            }, entrypoint));
            this.root.setLoading(false);
            failed(e);
        }
    }
}
